import React from 'react';
import { Icons } from './Icons';
import { ValidationResult } from '../types';

interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: ValidationResult | null;
  isLoading: boolean;
}

const ValidationModal: React.FC<ValidationModalProps> = ({
  isOpen,
  onClose,
  result,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-slate-800 shadow-2xl transition-all border border-slate-200 dark:border-slate-700 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center">
            <Icons.Activity className="w-5 h-5 mr-2 text-indigo-500" />
            Validate & Dry Run
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icons.Spinner className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-300 font-medium">
                Analyzing configuration...
              </p>
              <p className="text-sm text-slate-400 mt-2">
                Checking syntax and simulating resource changes.
              </p>
            </div>
          ) : result ? (
            <div className="space-y-6">
              {/* Status Banner */}
              <div
                className={`rounded-lg p-4 border flex items-start ${
                  result.isValid
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}
              >
                <div className="shrink-0 mr-3 mt-0.5">
                  {result.isValid ? (
                    <Icons.ShieldCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <Icons.Warning className="w-6 h-6 text-red-600 dark:text-red-400" />
                  )}
                </div>
                <div>
                  <h4
                    className={`text-base font-bold ${
                      result.isValid
                        ? 'text-green-800 dark:text-green-300'
                        : 'text-red-800 dark:text-red-300'
                    }`}
                  >
                    {result.isValid ? 'Configuration Valid' : 'Issues Detected'}
                  </h4>
                  <p
                    className={`text-sm mt-1 ${
                      result.isValid
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-red-700 dark:text-red-400'
                    }`}
                  >
                    {result.isValid
                      ? 'No syntax errors found in the generated files.'
                      : 'There are potential problems with the generated configuration.'}
                  </p>
                </div>
              </div>

              {/* Simulated Plan Summary */}
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-2">
                  Simulated Dry Run
                </h4>
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-700 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-mono">
                  {result.summary}
                </div>
              </div>

              {/* Issues List */}
              {result.issues && result.issues.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-2">
                    Syntax Issues
                  </h4>
                  <ul className="space-y-2">
                    {result.issues.map((issue, idx) => (
                      <li
                        key={idx}
                        className="flex items-start text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded border border-slate-100 dark:border-slate-700 shadow-sm"
                      >
                        <Icons.Code className="w-4 h-4 text-slate-400 mr-2 mt-0.5 shrink-0" />
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Formatting Issues */}
              {result.formattingIssues && result.formattingIssues.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-2 flex items-center">
                    <Icons.AlertCircle className="w-4 h-4 mr-2 text-yellow-500" />
                    Formatting Issues ({result.formattingIssues.length})
                  </h4>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    These are warnings only - the code is syntactically valid but doesn't follow HCL
                    formatting standards.
                  </div>
                  <ul className="space-y-2 max-h-60 overflow-y-auto">
                    {result.formattingIssues.map((fIssue, idx) => (
                      <li
                        key={idx}
                        className="flex flex-col text-xs text-slate-600 dark:text-slate-400 bg-yellow-50 dark:bg-yellow-900/10 p-3 rounded border border-yellow-200 dark:border-yellow-800/30"
                      >
                        <div className="flex items-start mb-1">
                          <span className="font-mono font-semibold text-yellow-700 dark:text-yellow-400 mr-2">
                            {fIssue.file}.tf:{fIssue.line}
                          </span>
                          <span>{fIssue.issue}</span>
                        </div>
                        {fIssue.suggestion && (
                          <div className="ml-4 text-slate-500 dark:text-slate-500 italic">
                            💡 {fIssue.suggestion}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-slate-500">No results available.</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            Close Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;
